import { gscTestQuery } from '../../GSCTestData/query';
import { DATA_STATE, DEFAULT_SEARCH_TYPE } from './period';

/**
 * Search Console permission levels that carry Performance rights. Owner is not required — full
 * and restricted users can read the report too — so the list is explicit rather than a check for
 * ownership. Both the camelCase values the REST reference returns and the upper-case enum the
 * discovery document documents are accepted.
 */
export const ELIGIBLE_PERMISSIONS = new Set([
    'siteOwner',
    'siteFullUser',
    'siteRestrictedUser',
    'SITE_OWNER',
    'SITE_FULL_USER',
    'SITE_RESTRICTED_USER',
]);

/**
 * The most rows one Search Analytics request may ask for. It is the API's own ceiling, so a
 * report that wants more has to page with `startRow` rather than raise this.
 */
export const MAX_ROW_LIMIT = 25000;

/**
 * Builds one Search Analytics request body.
 *
 * Country and device arrive as already-validated enum values from the controls; `filters` carries
 * any further rules a report lets the user add. All of them go into a single "and" group, which
 * is omitted entirely when there are none — an empty dimensionFilterGroups is not the same
 * request as no filter at all. AND is the only group type offered: an OR group changes what a
 * returned row means, and no report here claims to support one.
 *
 * The search type picks the surface the whole report reads, so it travels with every request
 * rather than being assumed: a page row from Discover and a page row from Web Search are counts
 * of different things and must never be mixed inside one report.
 */
export function buildResource({
    period,
    dimensions,
    aggregationType,
    dataState = DATA_STATE.FINAL,
    searchType = DEFAULT_SEARCH_TYPE,
    country,
    device,
    filters = [],
    rowLimit = MAX_ROW_LIMIT,
    startRow = 0,
}) {
    const group = [];
    if (country) group.push({ dimension: 'country', operator: 'equals', expression: country });
    if (device) group.push({ dimension: 'device', operator: 'equals', expression: device });
    for (const filter of filters) {
        if (filter?.dimension && filter?.operator && filter?.expression) {
            group.push({
                dimension: filter.dimension,
                operator: filter.operator,
                expression: filter.expression,
            });
        }
    }

    const resource = {
        startDate: period.startDate,
        endDate: period.endDate,
        type: searchType,
        dataState,
        dimensions,
        aggregationType,
        rowLimit,
        startRow,
    };

    if (group.length) {
        resource.dimensionFilterGroups = [{ groupType: 'and', filters: group }];
    }

    return resource;
}

/**
 * Sends queries through the live GSC proxy. The endpoint forwards each raw Search Analytics body
 * to the Cloud Function using the OAuth credentials linked to `domain_id`, which is why every
 * call needs a credentials domain even though the property being queried is named per query.
 *
 * Several queries can share one call; they then also share its fate, so queries whose failure
 * must not take the others down with it are sent separately by their callers.
 */
export async function runQueries({ credentialsDomainId, siteUrl, queries, maxCount }) {
    const response = await gscTestQuery({
        domain_id: credentialsDomainId,
        queries: queries.map(({ key, resource }) => ({ key, resource, domain: siteUrl })),
        max_count: maxCount,
    });

    const data = await response.json();

    if (data?.status !== 200) {
        throw new Error(data?.message ?? data?.detail ?? 'GSC query failed');
    }

    return data?.results ?? {};
}

/** Rows per keyed request, and the ceiling a report will page up to across all of them. */
export const ROW_CHUNK_SIZE = 5000;
export const ROW_CAP = 10000;

/**
 * One chunk of rows for one period, keyed by a single dimension (page, query, country, ...).
 *
 * Paging is bounded and explicit: the first chunk starts at row 0 and further "load more" calls
 * may reach `cap`, after which retrieval stops. `stopReason` distinguishes the three ways that
 * happens, because "the last response was empty" and "the product cap was reached" mean very
 * different things about how much of the property the table is showing — and neither proves the
 * property has no more rows, since Search Console withholds rows below its own thresholds.
 *
 * The offset advances by the size requested rather than the number of rows returned, so a short
 * chunk cannot make the next request re-read rows this one already has.
 *
 * `auto` aggregation is used rather than `byProperty`: these rows are per key, and a keyed row
 * can never be labelled whole-property traffic.
 */
export async function fetchRowChunk({
    credentialsDomainId,
    siteUrl,
    period,
    dimension,
    searchType,
    country,
    device,
    dataState,
    filters = [],
    offset = 0,
    chunkSize = ROW_CHUNK_SIZE,
    cap = ROW_CAP,
}) {
    const results = await runQueries({
        credentialsDomainId,
        siteUrl,
        maxCount: chunkSize,
        queries: [
            {
                key: 'rows',
                resource: buildResource({
                    period,
                    dimensions: [dimension],
                    aggregationType: 'auto',
                    dataState,
                    searchType,
                    country,
                    device,
                    filters,
                    rowLimit: chunkSize,
                    startRow: offset,
                }),
            },
        ],
    });

    const rows = results.rows ?? [];
    const nextOffset = offset + chunkSize;

    let stopReason = 'more_available';
    if (!rows.length) stopReason = 'empty_page';
    else if (nextOffset >= cap) stopReason = 'product_cap';

    return {
        rows,
        returnedRows: rows.length,
        requestedRows: chunkSize,
        offset,
        nextOffset: stopReason === 'more_available' ? nextOffset : null,
        stopReason,
    };
}

/**
 * The daily series for one period under whatever filters are in force.
 *
 * Kept separate from the keyed request because it answers a different question — how the window
 * got to its total, not which keys make it up — and because a trend that fails must not take the
 * table with it.
 */
export async function fetchDailySeries({
    credentialsDomainId,
    siteUrl,
    period,
    searchType,
    country,
    device,
    dataState,
    filters = [],
    aggregationType = 'auto',
}) {
    const results = await runQueries({
        credentialsDomainId,
        siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'daily',
                resource: buildResource({
                    period,
                    dimensions: ['date'],
                    aggregationType,
                    dataState,
                    searchType,
                    country,
                    device,
                    filters,
                }),
            },
        ],
    });

    return results.daily ?? [];
}

/**
 * Optional default-date probe: the most recent date the property has any activity on.
 *
 * Run unfiltered and against final data once a property is picked, purely to seed the date
 * controls with a window that has something in it. The date it finds is the latest date with
 * returned activity, not a guarantee that everything up to it is final — which is why it only
 * suggests dates and is overridden the moment the user sets their own.
 */
export async function probeLatestDate({ credentialsDomainId, siteUrl, searchType, days = 14 }) {
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (days - 1));

    const iso = (value) => value.toISOString().slice(0, 10);

    const results = await runQueries({
        credentialsDomainId,
        siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'probe',
                resource: buildResource({
                    period: { startDate: iso(startDate), endDate: iso(endDate) },
                    dimensions: ['date'],
                    aggregationType: 'byProperty',
                    dataState: DATA_STATE.FINAL,
                    searchType,
                }),
            },
        ],
    });

    const dates = (results.probe ?? [])
        .map((row) => row?.keys?.[0])
        .filter(Boolean)
        .sort();

    return dates.length ? dates[dates.length - 1] : null;
}
